import { createSelector } from '@reduxjs/toolkit';

import {
    TaxProfileState,
    TaxProfile,
    TaxDocument,
    TAX_PROFILE_STATUS
} from '../types/payout.types';

interface RootState {
    tax: TaxProfileState;
}

export const selectTaxProfileState = (state: RootState) => state.tax;

export const selectTaxProfileLoading: (state: RootState) => boolean = createSelector(
    selectTaxProfileState,
    (taxProfileState): boolean => taxProfileState.loading
);

export const selectTaxInterviewUrl: (state: RootState) => string | undefined = createSelector(
    selectTaxProfileState,
    (taxProfileState): string | undefined => taxProfileState.taxInterviewUrl
);

export const selectInterviewFailed: (state: RootState) => boolean = createSelector(
    selectTaxProfileState,
    (taxProfileState): boolean => taxProfileState.interviewFailed || false
);

export const selectTaxProfile: (state: RootState) => TaxProfile | undefined = createSelector(
    selectTaxProfileState,
    (taxProfileState): TaxProfile | undefined => taxProfileState.taxProfile
);

export const selectTaxProfileError: (state: RootState) => string | null | undefined =
    createSelector(
        selectTaxProfileState,
        (taxProfileState): string | null | undefined => taxProfileState.error
    );

export const selectAccountStatus: (state: RootState) => string | undefined = createSelector(
    selectTaxProfileState,
    (taxProfileState): string | undefined => taxProfileState.accountStatus
);

export const selectIsValidatedTaxProfile: (state: RootState) => boolean = createSelector(
    selectTaxProfileState,
    (taxProfileState): boolean => Boolean(taxProfileState?.taxProfile?.recordIdentityKey)
);

export const selectAccountStatusLoading: (state: RootState) => boolean = createSelector(
    selectTaxProfileState,
    (taxProfileState): boolean => taxProfileState.accountStatusLoading || false
);

export const selectTaxProfileStatus: (state: RootState) => string | undefined = createSelector(
    selectTaxProfileState,
    (taxProfileState): string | undefined => taxProfileState?.taxProfileStatus
);

export const selectTaxDocumentsLoading: (state: RootState) => boolean = createSelector(
    selectTaxProfileState,
    (taxProfileState): boolean => taxProfileState.taxDocumentsLoading || false
);

export const selectNoTaxProfile: (state: RootState) => boolean = createSelector(
    selectTaxProfileStatus,
    (taxProfileStatus): boolean => taxProfileStatus === TAX_PROFILE_STATUS.NULL
);

export const selectTaxDocuments: (state: RootState) => TaxDocument[] | undefined = createSelector(
    selectTaxProfileState,
    (taxProfileState): TaxDocument[] | undefined => taxProfileState.taxDocuments
);

export const selectSelectedTaxYear: (state: RootState) => number | undefined = createSelector(
    selectTaxProfileState,
    (taxProfileState): number | undefined => taxProfileState.selectedTaxYear
);

export const selectYearLength: (state: RootState) => number | undefined = createSelector(
    selectTaxProfileState,
    (taxProfileState): number | undefined => taxProfileState.yearLength
);

export const selectFilteredTaxDocuments: (state: RootState) => TaxDocument[] = createSelector(
    selectTaxDocuments,
    selectSelectedTaxYear,
    (taxDocuments, selectedTaxYear): TaxDocument[] => {
        if (!taxDocuments || !selectedTaxYear) return [];
        return taxDocuments.filter((doc) => doc.taxYear === selectedTaxYear);
    }
);
